import Button from "common/Buttons/Button";
import Steps from "common/Steps/Steps";
import useNumUsersSelector from "features/admin/group/InvitationModal/InvitationSelectors/NumUsersSelector";
import useSubscriptionDurationSelector from "features/admin/group/InvitationModal/InvitationSelectors/SubscriptionDurationSelector";
import { useState } from "react";
import InvitationSummary from "features/admin/group/InvitationModal/InvitationSummary";
import { FixMeLater } from "types";
import Modal from "common/Modal/Modal";
import { openMessage } from "utils/misc";
import { Invitation, LanguageNameSpaces } from "@shared/types";
import { sharedLib } from "@shared/services/shared.service";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";

interface InvitationModalProps {
  subGroupID: number;
  closeModal: () => void;
  invitation?: Invitation;
}

const InvitationModal = ({
  subGroupID,
  closeModal,
  invitation,
}: InvitationModalProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.ADMIN);
  const [step, setStep] = useState(0);
  const [
    numUsersSelectorComponent,
    numUsers,
    username,
    telegramUserId,
    numUsersError,
  ] = useNumUsersSelector(invitation);
  const [daysDurationSelectorComponent, subscriptionDuration] =
    useSubscriptionDurationSelector(invitation);
  const [modifiedInvitation, setModifiedInvitation] = useState<
    Invitation | undefined
  >();
  const [createInvitation, createInvitationStatus] =
    sharedLib.sharedApi.admin.useCreateInvitationMutation();

  const [updateInvitation, updateInvitationStatus] =
    sharedLib.sharedApi.admin.useUpdateInvitationMutation();

  const handleSaveInvitation = () => {
    const func = invitation ? updateInvitation : createInvitation;
    const data: any = {
      username: numUsers > 1 ? null : username,
      telegram_user_id: numUsers > 1 ? null : telegramUserId,
      num_users: numUsers,
      subscription_duration: subscriptionDuration,
      sub_group_id: subGroupID,
    };
    if (invitation) {
      data.invitation_id = invitation.id;
    }
    func(data)
      .unwrap()
      .then((modifiedInvitation) => {
        openMessage(t("invitations.modal.createSuccess"), "success");
        setStep(1);
        setModifiedInvitation(modifiedInvitation);
      })
      .catch((error: FixMeLater) => {
        openMessage(
          error.message ? error.message : t("invitations.modal.createError"),
          "error"
        );
      });
  };

  const formValid = !numUsersError;

  const invitationModified = () => {
    if (!invitation) {
      return true;
    }
    return (
      invitation.num_users !== numUsers ||
      invitation.username !== username ||
      invitation.telegram_user_id !== telegramUserId ||
      invitation.subscription_duration !== subscriptionDuration
    );
  };

  const checkButtonValid = () => {
    return formValid && invitationModified();
  };

  const footer = [
    <Button key="cancel" onClick={closeModal}>
      {step === 0
        ? t("invitations.modal.cancel")
        : t("invitations.modal.close")}
    </Button>,
  ];
  if (step === 0) {
    footer.push(
      <Button
        key="create"
        type="primary"
        onClick={handleSaveInvitation}
        disabled={!checkButtonValid()}
        loading={
          createInvitationStatus.isLoading || updateInvitationStatus.isLoading
        }
      >
        {step === 0
          ? invitation
            ? t("invitations.modal.save")
            : t("invitations.modal.create")
          : t("invitations.modal.ok")}
      </Button>
    );
  }

  const items = [
    {
      title: invitation
        ? t("invitations.modal.editStep")
        : t("invitations.modal.createStep"),
    },
    { title: t("invitations.modal.shareStep") },
  ];

  return (
    <Modal
      onCancel={closeModal}
      open={true}
      title={
        invitation
          ? t("invitations.modal.editTitle")
          : t("invitations.modal.createTitle")
      }
      footer={footer}
    >
      <Steps current={step} items={items} style={{ marginBottom: "24px" }} />
      {
        {
          0: (
            <>
              {numUsersSelectorComponent}
              {daysDurationSelectorComponent}
            </>
          ),
          1: (
            <InvitationSummary
              invitation={modifiedInvitation}
              created={!invitation}
            />
          ),
        }[step]
      }
    </Modal>
  );
};

export default InvitationModal;
